import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  submitAllianceTaskVote,
  type AllianceTaskOptionId,
  type AllianceTaskPeriodClient,
} from '../../common/allianceApi'
import {
  ALLIANCE_TASK_OPTION_META,
  ALLIANCE_TASK_OPTION_ORDER,
  ALLIANCE_TASK_TITLE_STYLE,
  ALLIANCE_TASK_VOTE_ICON,
} from '../../common/allianceTaskOptionsClient'
import { alert } from '../Alert/Alert'

interface AllianceTaskVoteModalProps {
  open: boolean
  address: string
  period: AllianceTaskPeriodClient
  onClose: () => void
  onVoted: (updated: AllianceTaskPeriodClient) => void
}

const BASE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/voting-modal'
const MODAL_BG = `${BASE}/modal-bg.webp`
const CARD_BG = `${BASE}/card-bg.webp`
const CLOSE_BTN = `${BASE}/close-btn.webp`
const CINZEL = '"Cinzel", "Times New Roman", serif'

/**
 * Member vote modal — set the alliance's weekly strategic task. Single, changeable choice
 * among the 5 fixed options while voting is open. Parchment art from assets/alliance-task/voting-modal.
 * The parchment stretches to wrap the content (body fills the frame); each option's medallion icon
 * is a little taller than its card and overlaps it (protrudes top/bottom, stays inside horizontally).
 */
export const AllianceTaskVoteModal: React.FC<AllianceTaskVoteModalProps> = ({
  open,
  address,
  period,
  onClose,
  onVoted,
}) => {
  const { t } = useTranslation()
  const [busy, setBusy] = useState<AllianceTaskOptionId | null>(null)
  const [myVote, setMyVote] = useState<AllianceTaskOptionId | null>(period.myVote)

  if (!open || typeof document === 'undefined') return null

  const handlePick = async (optionId: AllianceTaskOptionId) => {
    if (busy) return
    setBusy(optionId)
    try {
      const res = await submitAllianceTaskVote(address, optionId)
      setMyVote(optionId)
      onVoted(res.taskPeriod)
      alert.open(t('alliance.taskVoteSaved', { defaultValue: 'Your vote has been recorded.' }))
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      alert.open(e?.response?.data?.message || e?.message || t('alliance.taskVoteFailed', { defaultValue: 'Could not save your vote.' }))
    } finally {
      setBusy(null)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[220] flex items-start justify-center overflow-y-auto bg-black/70 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative my-auto w-full select-none"
        style={{
          width: 'min(96vw, 600px)',
          containerType: 'inline-size',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          padding: '3.5% 3% 3.5%',
          filter: 'drop-shadow(0 18px 48px rgba(0,0,0,0.6))',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Close (top-right corner) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute transition-[filter,transform] hover:brightness-110 active:scale-95"
          style={{ top: '2.5%', right: '1%', width: '12cqw', height: '12cqw', zIndex: 5 }}
          aria-label={t('common.close', { defaultValue: 'Close' })}
        >
          <img src={CLOSE_BTN} alt="" draggable={false} className="h-full w-full object-contain" />
        </button>

        {/* Title */}
        <h2 className="text-center" style={{ ...ALLIANCE_TASK_TITLE_STYLE, fontSize: '6.2cqw' }}>
          {t('alliance.taskVoteTitle', { defaultValue: 'Set the Alliance Task' })}
        </h2>
        <p
          className="text-center"
          style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '3cqw', lineHeight: 1.3, color: '#5e4327', margin: '1.4cqw 0 0' }}
        >
          {t('alliance.taskVoteSubtitle', { defaultValue: 'Vote for this week’s strategy. You can change it until Tuesday.' })}
        </p>

        {/* Cards */}
        <div className="flex w-full flex-col" style={{ gap: '3cqw', marginTop: '5cqw' }}>
          {ALLIANCE_TASK_OPTION_ORDER.map((id) => {
            const meta = ALLIANCE_TASK_OPTION_META[id]
            const selected = myVote === id
            const isBusy = busy === id
            return (
              <button
                key={id}
                type="button"
                onClick={() => void handlePick(id)}
                disabled={busy !== null}
                className="relative flex items-center transition-[filter,transform] hover:brightness-[1.06] disabled:cursor-not-allowed"
                style={{
                  width: '100%',
                  height: '24cqw',
                  backgroundImage: `url(${CARD_BG})`,
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  padding: '0 5% 0 3%',
                  overflow: 'visible',
                  flexShrink: 0,
                  filter: selected
                    ? 'brightness(1.08) drop-shadow(0 0 9px rgba(240,212,136,0.9))'
                    : busy && !isBusy
                      ? 'brightness(0.9)'
                      : 'none',
                }}
              >
                {/* Medallion icon — taller than the card, overlapping it (protrudes top/bottom). */}
                <img
                  src={ALLIANCE_TASK_VOTE_ICON[id]}
                  alt=""
                  draggable={false}
                  className="shrink-0 object-contain"
                  style={{ height: '26cqw', width: 'auto', filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.5))', zIndex: 2 }}
                />

                <span className="min-w-0 flex-1 text-left" style={{ marginLeft: '3.5cqw' }}>
                  <span
                    className="block truncate"
                    style={{ fontFamily: CINZEL, fontWeight: 800, fontSize: '5.6cqw', lineHeight: 1.05, color: '#3a2010' }}
                  >
                    {t(meta.i18nKey, { defaultValue: meta.fallback })}
                  </span>
                  <span
                    className="block"
                    style={{ fontFamily: CINZEL, fontWeight: 500, fontSize: '3cqw', lineHeight: 1.15, color: '#6a5236', marginTop: '0.6cqw' }}
                  >
                    {t(meta.descKey, { defaultValue: meta.descFallback })}
                  </span>
                </span>

                {selected ? (
                  <span className="shrink-0" style={{ color: '#7a5a1e', fontSize: '6.5cqw' }}>✓</span>
                ) : isBusy ? (
                  <span className="shrink-0" style={{ color: '#7a5a1e', fontSize: '5cqw' }}>…</span>
                ) : null}
              </button>
            )
          })}
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default AllianceTaskVoteModal
